import { useState } from "react";

import {
  CheckCircle2,
  ChevronDown,
  Download,
  Eye,
  FileCheck2,
  FileText,
  Sparkles,
} from "lucide-react";

import { Button } from "@components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

type Props = {
  onPreview: () => void;
  onToggleQA: () => void;
  onDownloadDocx: () => void;
  onDownloadPdf: () => void;
  /** Doctor: open the Finalise Report confirmation (MDRS-271). */
  onFinalise: () => void;
  isDownloading: boolean;
  isDownloadingPdf: boolean;
  isFinalising: boolean;
  /** PDF/finalise needs a saved draft response; disabled until one exists. */
  canDownloadPdf: boolean;
  /** True once the report has been finalised — locks the finalise action. */
  finalised: boolean;
  /**
   * Admins get the download path as the primary action (PDF, with .docx on the
   * caret). Doctors get "Finalise Report" as the primary action instead.
   * Defaults to the doctor experience.
   */
  isAdmin?: boolean;
  /**
   * Whether to offer the .docx download on the caret (MDRS-336). Mirrors the
   * server gate — `report:download_docx`, on by default for doctors. MDRS-271
   * had removed the doctor's caret entirely to close the export back door;
   * MDRS-336 puts the Word download back, so the doctor's Finalise CTA carries
   * the same split-button shape the admin's download CTA has. PDF stays
   * admin-only either way. Admins are unaffected by this flag.
   */
  canDownloadDocx?: boolean;
  /** Whether to offer the Report QA button (MDRS-443). Mirrors the server
   * gate — `report_qa:use`, on by default for doctors. */
  canUseReportQA?: boolean;
  /** The case's documents are still being uploaded or summarised — holds back
   * Finalise until they settle (MDRS-508). The server enforces it too. */
  isCaseProcessing?: boolean;
  /** The processing status couldn't be checked at all — not the same as
   * "nothing arriving", so Finalise is held back too (MDRS-508). */
  isProcessingStatusUnavailable?: boolean;
};

export const HeaderActions = ({
  onPreview,
  onToggleQA,
  onDownloadDocx,
  onDownloadPdf,
  onFinalise,
  isDownloading,
  isDownloadingPdf,
  isFinalising,
  canDownloadPdf,
  finalised,
  isAdmin = false,
  canDownloadDocx = false,
  canUseReportQA = false,
  isCaseProcessing = false,
  isProcessingStatusUnavailable = false,
}: Props) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const busy = isDownloading || isDownloadingPdf || isFinalising;

  // Why the doctor can't finalise yet, if there's a reason worth telling them.
  // A disabled button gets no pointer events and can't take focus, so a
  // `title` on it never shows — the reason goes in a tooltip on a focusable
  // wrapper instead (same pattern as MapToReportBuilderDialog).
  const finaliseDisabledReason = isCaseProcessing
    ? "Documents are still being uploaded or processed — you can finalise once they've finished"
    : isProcessingStatusUnavailable
      ? "We couldn't check whether documents are still arriving — retry from the notice on this case"
      : !canDownloadPdf
        ? "Save a draft first to finalise"
        : null;

  const finaliseButton = (
    <Button
      size="sm"
      className={canDownloadDocx ? "rounded-r-none" : undefined}
      onClick={onFinalise}
      disabled={busy || !!finaliseDisabledReason}
      isLoading={isFinalising}
      title={
        finaliseDisabledReason
          ? undefined
          : "Finalise and submit this report to MEDirect"
      }
    >
      <FileCheck2 className="mr-1.5 size-4" />
      Finalise Report
    </Button>
  );

  // The .docx caret, shared by every CTA that can carry it (MDRS-336): the
  // admin's download button, the doctor's finalise button, and the finalised
  // pill. `outline` matches the caret to the pill's outline variant — the
  // default filled styling only reads correctly next to a filled button.
  const renderDocxMenu = (outline = false) => (
    <Popover open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant={outline ? "simple-outline" : "default"}
          className={
            outline
              ? "rounded-l-none border-l-0 px-2"
              : "rounded-l-none border-l border-l-white/20 px-2"
          }
          // Downloading needs a saved draft response, same precondition as the
          // primary buttons beside it — without this the caret is clickable and
          // dead-ends on a "Save the draft first" toast. `outline` is the
          // finalised pill, where a draft necessarily exists already.
          disabled={busy || (!outline && !canDownloadPdf)}
          aria-label="More download options"
          title={
            !outline && !canDownloadPdf
              ? "Save a draft first to download"
              : "More download options"
          }
        >
          <ChevronDown className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-48 p-1">
        <button
          type="button"
          onClick={() => {
            setMenuOpen(false);
            onDownloadDocx();
          }}
          disabled={isDownloading}
          className="hover:bg-muted flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm disabled:opacity-50"
        >
          <FileText className="size-4" />
          Download .docx
        </button>
      </PopoverContent>
    </Popover>
  );

  return (
    <div className="flex items-center gap-2">
      <Button variant="simple-outline" size="sm" onClick={onPreview}>
        <Eye className="mr-1.5 size-4" />
        Preview
      </Button>

      {/* Report QA — secondary (blue), faint tinted bg. Was accent (purple),
          now deprecated. */}
      {canUseReportQA ? (
        <Button variant="secondary-subtle" size="sm" onClick={onToggleQA}>
          <Sparkles className="mr-1.5 size-4" />
          Report QA
        </Button>
      ) : null}

      {finalised ? (
        /* Finalised & locked — read-only indicator for either role. The
           frozen-PDF download + Vinci lifecycle (mark-uploaded, unlock) live in
           the admin finalisation bar below, so no PDF here.

           MDRS-336: the .docx caret stays available on a locked case. The
           finalise lock is a *write* lock and this is a read, so the server
           allows it — and the Draft Report editor's Download link is visible in
           this state too, so hiding it here would make the two surfaces
           disagree about the same rule. Note the .docx is a re-render of the
           stored draft rather than the frozen PDF artifact; the case being
           locked is what keeps the content equivalent. */
        <div className="flex">
          <Button
            size="sm"
            variant="simple-outline"
            disabled
            className={canDownloadDocx ? "rounded-r-none" : undefined}
            title="This report has been finalised and locked"
          >
            <CheckCircle2 className="mr-1.5 size-4 text-success" />
            Report Finalised
          </Button>
          {canDownloadDocx ? renderDocxMenu(true) : null}
        </div>
      ) : isAdmin ? (
        /* Admin, pre-finalise: download split button (PDF primary, .docx caret). */
        <div className="flex">
          <Button
            size="sm"
            className="rounded-r-none"
            onClick={onDownloadPdf}
            disabled={busy || !canDownloadPdf}
            isLoading={isDownloadingPdf}
            title={
              canDownloadPdf ? "Download PDF report" : "No report available yet"
            }
          >
            <Download className="mr-1.5 size-4" />
            Download PDF Report
          </Button>
          {renderDocxMenu()}
        </div>
      ) : (
        /* Doctor, pre-finalise — finalise stays the primary action (finalised
           is handled by the first branch above, for both roles). MDRS-336 adds
           the .docx download on the caret, so the doctor's CTA has the same
           split-button shape as the admin's. PDF is never offered here. */
        <div className="flex">
          {finaliseDisabledReason ? (
            <Tooltip>
              <TooltipTrigger asChild>
                {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
                <span tabIndex={0} className="inline-flex">
                  {finaliseButton}
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">{finaliseDisabledReason}</TooltipContent>
            </Tooltip>
          ) : (
            finaliseButton
          )}
          {canDownloadDocx ? renderDocxMenu() : null}
        </div>
      )}
    </div>
  );
};
